import React, {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { MessageSquareTextIcon } from "lucide-react";

import { SearchIndexObject } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { List } from "@/components/organisms/List";
import { Mutations, Queries, Values } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useEnumColor } from "@/hooks/useEnumColor";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { icons, iconsByTypename } from "@/objects/@icons";
import { Objects } from "@/objects";
import { State } from "@/state";
import { CommandDefinitionType } from "@/state/@types";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import _ from "lodash";
import { Input } from "@/components/ui/input";

export const COMMAND_ID = "search";

/** The search command's body (registered on mount): no taller than its list, 800px. */
const SEARCH_COMMAND_PALETTE_CONTENT_CLASS = "max-h-[800px] overflow-hidden";

export type SearchContextType = Record<string, never>;

function isSearchIndexObject(
  item: CommandDefinitionType | SearchIndexObject,
): item is SearchIndexObject {
  return (
    typeof item === "object" &&
    item != null &&
    "__typename" in item &&
    item.__typename === "SearchIndexObject"
  );
}

function getSearchResultNodeTypename(
  result: SearchIndexObject,
): string | undefined {
  return (result.node as { __typename?: string } | null | undefined)
    ?.__typename;
}

function getSearchResultIcon(result: SearchIndexObject) {
  const typename = getSearchResultNodeTypename(result);
  if (!typename) {
    return icons.search;
  }
  return (
    Objects[typename as keyof typeof Objects]?.ReactIcon ??
    iconsByTypename[typename as keyof typeof iconsByTypename] ??
    icons.search
  );
}

function getSearchResultTitle(
  result: SearchIndexObject,
): string | React.ReactNode {
  const typename = getSearchResultNodeTypename(result);
  if (!typename || !result.node) {
    return "Result";
  }
  const objectDef = Objects[typename as keyof typeof Objects];
  if (objectDef?.getTitle) {
    return objectDef.getTitle(result.node as never);
  }
  const node = result.node as { name?: string; title?: string };
  return node.name ?? node.title ?? typename;
}

/**
 * Applies search highlighting to string leaves while preserving element structure
 * (e.g. muted <span> prefixes in command titles).
 */
function highlightMatchInReactNode(
  node: React.ReactNode,
  search: string | null | undefined,
  highlightSegment: (text: string) => React.ReactNode,
): React.ReactNode {
  if (!search || search.trim() === "") {
    return node;
  }
  if (node == null || typeof node === "boolean") {
    return node;
  }
  if (typeof node === "string" || typeof node === "number") {
    return highlightSegment(String(node));
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <Fragment key={i}>
        {highlightMatchInReactNode(child, search, highlightSegment)}
      </Fragment>
    ));
  }
  if (!isValidElement(node)) {
    return node;
  }

  // React 19's ``isValidElement`` narrows ``props`` to ``unknown``;
  // assert the children-bearing shape we rely on here.
  const { type, props } = node as ReactElement<{ children?: ReactNode }>;
  if (type === Fragment) {
    return (
      <Fragment>
        {Children.map(props.children, (child) =>
          highlightMatchInReactNode(child, search, highlightSegment),
        )}
      </Fragment>
    );
  }

  return cloneElement(node as ReactElement<{ children?: ReactNode }>, {
    children: Children.map(props.children, (child) =>
      highlightMatchInReactNode(child, search, highlightSegment),
    ),
  });
}

function Header({ commandInstanceId }: { commandInstanceId: string }) {
  const setCommandSearchValue = State((state) => state.setCommandSearchValue);
  const commandCenterEnterHandler = State(
    (state) => state.commandCenterEnterHandler,
  );
  const isCommandCenterSearching = State(
    (state) => state.isCommandCenterSearching,
  );

  const handleEnter = useCallback(() => {
    if (commandCenterEnterHandler) {
      commandCenterEnterHandler();
    }
  }, [commandCenterEnterHandler]);

  return (
    <div className="w-full h-fit flex flex-row justify-between flex-nowrap items-start">
      <div className="grow pb-0.5">
        <Input
          autoFocus
          placeholder="Search for quick actions or records..."
          onChange={(e) => setCommandSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
              e.preventDefault();
              handleEnter();
            }
          }}
          className="border-0 bg-transparent shadow-none dark:bg-transparent focus-visible:ring-0"
        />
      </div>
      <div className="p-2 flex items-center justify-center h-8">
        {(() => {
          return isCommandCenterSearching !== false ? <Spinner /> : null;
        })()}
      </div>
    </div>
  );
}

function Component({
  commandInstanceId,
  commandDefinitionId,
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: SearchContextType;
}) {
  const commandSearchValue = State((state) => state.commandSearchValue);
  const setCommandSearchValue = State((state) => state.setCommandSearchValue);
  const setCommandCenterEnterHandler = State(
    (state) => state.setCommandCenterEnterHandler,
  );
  const popCommandsFromStack = State((state) => state.popCommandsFromStack);
  const openThreadPanel = State((state) => state.openThreadPanel);
  const { userEntity } = useScope();

  // Mutation to create a new thread
  const { mutate: createThread } = useBackendMutation({
    mutation: Mutations.CREATE_NODE,
    callbacks: {
      onSuccess: (data: any) => {
        const thread = Array.isArray(data) ? data?.[0] : data?.createNode?.data;
        if (thread?.__typename !== "ThreadObject") return;
        State.getState().setActiveThreadNodeId(thread.id);
      },
    },
    refetchQueries: ["ThreadsQuery"],
  });

  // Handler to start a new thread with the current search value
  const handleStartThread = useCallback(() => {
    if (!commandSearchValue?.trim()) return;

    // Close the command center
    popCommandsFromStack();
    State.setState({ dialogSize: null });

    // Open the thread panel so the new thread is immediately visible.
    openThreadPanel();

    // Create a new thread with the search value as the prompt
    createThread({
      variables: {
        input: {
          entityIdentifier: userEntity?.identifier,
          node: {
            thread: {
              prompt: [{ text: commandSearchValue.trim() }],
              context: [],
            },
          },
        },
      },
    });
  }, [
    commandSearchValue,
    popCommandsFromStack,
    openThreadPanel,
    createThread,
    userEntity?.identifier,
  ]);

  const activeIndexRef = useRef<{
    data: CommandDefinitionType | SearchIndexObject;
    onClick: () => void;
  }>({ data: null, onClick: () => {} });

  // Search the API for results
  const [debouncedCommandSearchValue, setDebouncedCommandSearchValue] =
    useState(commandSearchValue);
  const debouncedSetCommandSearchValue = useMemo(
    () =>
      _.debounce((search: string) => {
        setDebouncedCommandSearchValue(search);
      }, 500),
    [setDebouncedCommandSearchValue],
  );

  useEffect(() => {
    if (commandSearchValue != null && commandSearchValue.trim() !== "") {
      State.setState({ isCommandCenterSearching: null });
      setTimeout(() => {
        const previousIsCommandCenterSearching =
          State.getState().isCommandCenterSearching;
        if (previousIsCommandCenterSearching === null) {
          State.setState({
            isCommandCenterSearching: false,
          });
        }
      }, 500 + 100);
    }
    if (commandSearchValue == null || commandSearchValue.trim() === "") {
      setCommandSearchValue("");
      setDebouncedCommandSearchValue("");
    } else {
      debouncedSetCommandSearchValue(commandSearchValue);
    }
  }, [commandSearchValue, debouncedSetCommandSearchValue]);

  const { data, loading } = useBackendQuery(Queries.SEARCH_QUERY, {
    variables: {
      queryStr: debouncedCommandSearchValue,
      startDate: null,
      endDate: null,
      entityIdentifiers: null,
    },
  });

  useEffect(() => {
    State.setState({ isCommandCenterSearching: loading });
  }, [loading]);

  const searchResults =
    commandSearchValue != null && commandSearchValue.trim() !== ""
      ? (data?.search?.data ?? [])
      : [];

  const commandPaletteFilterHaystack = useCallback(
    (command: CommandDefinitionType): string => {
      if (typeof command.searchText === "string" && command.searchText.trim()) {
        return command.searchText;
      }
      const fromGetTitle = command.getTitle?.();
      if (typeof fromGetTitle === "string") return fromGetTitle;
      if (typeof command.title === "string") return command.title;
      return "";
    },
    [],
  );

  const filterCommands = useCallback(
    (commands: CommandDefinitionType[], search: string) => {
      if (!search || search.trim() === "") {
        return commands.filter((command) => command.isHidden !== true);
      }

      const q = search.toLowerCase();
      return commands
        .filter((command) => command.isHidden !== true)
        .filter((command) => {
          const titleHaystack = commandPaletteFilterHaystack(command);
          if (titleHaystack.toLowerCase().includes(q)) return true;
          if (command.description?.toLowerCase().includes(q)) return true;
          return false;
        });
    },
    [commandPaletteFilterHaystack],
  );

  const unfilteredCommands = State((store) => store.commandDefinitions);
  const commands = filterCommands(unfilteredCommands, commandSearchValue).sort(
    (a, b) => {
      return a.id.localeCompare(b.id);
    },
  );

  const startThreadLabel = useMemo(() => {
    if (!commandSearchValue?.trim()) return null;
    let displayValue = commandSearchValue.trim();
    if (displayValue.length > 12) {
      displayValue = displayValue.slice(0, 12) + "...";
    }
    return `Start thread: "${displayValue}"`;
  }, [commandSearchValue]);

  // "Start thread" list item - only shown when there are no other results
  const startThreadListItem = useMemo(() => {
    if (!commandSearchValue?.trim()) return null;
    // Only show in list if there are no commands or search results
    if (commands.length > 0 || searchResults.length > 0) return null;
    return {
      id: "start-thread",
      title: startThreadLabel || "",
      isHidden: false,
      ReactIcon: MessageSquareTextIcon,
      onClick: handleStartThread,
    } as CommandDefinitionType;
  }, [
    commandSearchValue,
    commands.length,
    searchResults.length,
    startThreadLabel,
    handleStartThread,
  ]);

  useEffect(() => {
    return () => {
      setCommandSearchValue("");
      setCommandCenterEnterHandler(null);
    };
  }, [setCommandSearchValue, setCommandCenterEnterHandler]);

  const getTitle = useCallback(
    (
      command: CommandDefinitionType | SearchIndexObject,
    ): string | React.ReactNode => {
      if (isSearchIndexObject(command)) {
        return getSearchResultTitle(command);
      }
      const cmd = command as CommandDefinitionType;
      return cmd.getTitle ? cmd.getTitle() : cmd.title;
    },
    [],
  );

  // What was searched for, marked in each result.
  const matchColor = useEnumColor(EnumColors.ORANGE);
  const highlightMatch = useCallback(
    (text: string, search: string | null | undefined): React.ReactNode => {
      if (!search || search.trim() === "") {
        return text;
      }
      const searchTerm = search.trim().toLowerCase();
      const lowerText = text.toLowerCase();
      const index = lowerText.indexOf(searchTerm);
      if (index === -1) {
        return text;
      }
      const before = text.slice(0, index);
      const match = text.slice(index, index + searchTerm.length);
      const after = text.slice(index + searchTerm.length);
      return (
        <>
          {before}
          <span
            style={{
              backgroundColor: matchColor.backgroundColor,
              color: matchColor.color,
              borderRadius: 2,
              padding: "0 2px",
            }}
          >
            {match}
          </span>
          {after}
        </>
      );
    },
    [matchColor.backgroundColor, matchColor.color],
  );
  const getIcon = useCallback(
    (command: CommandDefinitionType | SearchIndexObject) => {
      if (isSearchIndexObject(command)) {
        return getSearchResultIcon(command);
      }
      return (command as CommandDefinitionType).ReactIcon;
    },
    [],
  );
  const onRowClick = useCallback(
    (command: CommandDefinitionType | SearchIndexObject) => {
      if (isSearchIndexObject(command)) {
        // This is a search result, not a command
        return;
      } else {
        // This is a command, execute its onClick
        (command as CommandDefinitionType).onClick?.();
      }
    },
    [],
  );

  // Organize items: contextual groups → Commands (ungrouped) → other groups → Results
  const organizedData = useMemo(() => {
    const itemsWithoutGroup: CommandDefinitionType[] = [];
    const itemsByGroup = new Map<string, CommandDefinitionType[]>();
    const groupPriorities = new Map<string, number>();
    const groupIsContextual = new Map<string, boolean>();

    const allItems = startThreadListItem
      ? [startThreadListItem]
      : [...commands, ...searchResults];

    const searchResultRows: SearchIndexObject[] = [];

    allItems.forEach((item) => {
      if (
        isSearchIndexObject(item as CommandDefinitionType | SearchIndexObject)
      ) {
        searchResultRows.push(item as SearchIndexObject);
        return;
      }
      const cmd = item as CommandDefinitionType;
      if (cmd.group && typeof cmd.group === "string") {
        if (!itemsByGroup.has(cmd.group)) {
          itemsByGroup.set(cmd.group, []);
        }
        itemsByGroup.get(cmd.group)!.push(cmd);
        const prevCtx = groupIsContextual.get(cmd.group) ?? false;
        groupIsContextual.set(cmd.group, prevCtx || Boolean(cmd.isContextual));
        if (typeof cmd.groupPriority === "number") {
          const current = groupPriorities.get(cmd.group) ?? 0;
          if (cmd.groupPriority > current) {
            groupPriorities.set(cmd.group, cmd.groupPriority);
          }
        }
      } else {
        itemsWithoutGroup.push(cmd);
      }
    });

    const sortGroupEntries = (
      entries: [string, CommandDefinitionType[]][],
    ): [string, CommandDefinitionType[]][] =>
      [...entries].sort(([a], [b]) => {
        const pa = groupPriorities.get(a) ?? 0;
        const pb = groupPriorities.get(b) ?? 0;
        if (pa !== pb) return pb - pa;
        return a.localeCompare(b);
      });

    const allGroupEntries = Array.from(itemsByGroup.entries());
    const contextualGroupEntries = sortGroupEntries(
      allGroupEntries.filter(([name]) => groupIsContextual.get(name)),
    );
    const standardGroupEntries = sortGroupEntries(
      allGroupEntries.filter(([name]) => !groupIsContextual.get(name)),
    );

    const flatList: (CommandDefinitionType | SearchIndexObject)[] = [];
    const groupStarts = new Map<number, string>();

    const appendGroup = (groupName: string, items: CommandDefinitionType[]) => {
      if (items.length === 0) return;
      groupStarts.set(flatList.length, groupName);
      flatList.push(...items);
    };

    contextualGroupEntries.forEach(([groupName, items]) =>
      appendGroup(groupName, items),
    );

    if (itemsWithoutGroup.length > 0) {
      groupStarts.set(
        flatList.length,
        startThreadListItem ? "Thread" : "Commands",
      );
      flatList.push(...itemsWithoutGroup);
    }

    standardGroupEntries.forEach(([groupName, items]) =>
      appendGroup(groupName, items),
    );

    if (searchResultRows.length > 0) {
      groupStarts.set(flatList.length, "Results");
      flatList.push(...searchResultRows);
    }

    return { items: flatList, groupStarts };
  }, [commands, searchResults, startThreadListItem]);

  const listData = useMemo(() => organizedData.items, [organizedData.items]);

  const dialogIsFullScreen = State((state) => state.dialogIsFullScreen);

  return (
    <>
      <div
        className={cn(
          "flex flex-col w-full",
          dialogIsFullScreen ? "max-h-none" : "max-h-200",
          "overflow-hidden",
        )}
      >
        <List
          data={listData}
          onItemClick={onRowClick}
          onActiveIndexChange={(data, onClick) => {
            activeIndexRef.current.data = data;
            activeIndexRef.current.onClick = onClick;
            setCommandCenterEnterHandler(onClick);
          }}
          fillHeight={false}
          className={cn(
            dialogIsFullScreen ? "max-h-none" : "max-h-200",
            // Its sides' room; above and below, the rows' own.
            "min-h-0 px-1",
          )}
          overscanCount={20}
        >
          <List.Items>
            {(command, index) => {
              // Show group header if this index is a group start
              const cmd = command as CommandDefinitionType | SearchIndexObject;
              const groupName = organizedData.groupStarts.get(index);
              const showGroupHeader = groupName !== undefined;

              return (
                <React.Fragment key={index}>
                  {showGroupHeader && <List.ItemGroup value={groupName!} />}
                  <List.Item value={cmd as any}>
                    <div className="flex items-center justify-center w-6.5 h-6.5 bg-muted [border-width:1px] [border-style:solid] rounded-sm border-border">
                      <Icon
                        ReactIcon={getIcon(cmd)}
                        size="md"
                        style={{
                          color:
                            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                        }}
                      />
                    </div>

                    <Typography
                      level="body-md"
                      className="text-center cursor-pointer inline-block"
                    >
                      {(() => {
                        const t = getTitle(cmd);
                        return typeof t === "string"
                          ? highlightMatch(t, commandSearchValue)
                          : highlightMatchInReactNode(
                              t,
                              commandSearchValue,
                              (segment) =>
                                highlightMatch(segment, commandSearchValue),
                            );
                      })()}
                    </Typography>
                  </List.Item>
                </React.Fragment>
              );
            }}
          </List.Items>
        </List>

        {/* Padded like the footer every other command gets from StickyLayout. */}
        <div className="shrink-0 [border-top-width:1px] [border-top-style:solid] border-border p-2">
          <CommandCenterListFooter
            buttons={[
              ...(startThreadLabel
                ? [
                    {
                      variant: "secondary" as const,
                      size: "sm" as const,
                      children: (
                        <>
                          <Icon ReactIcon={MessageSquareTextIcon} size="sm" />
                          {startThreadLabel}
                        </>
                      ),
                      onClick: handleStartThread,
                      hotkey: "meta+enter",
                    },
                  ]
                : []),
              {
                variant: "default" as const,
                size: "sm" as const,
                children: "Submit",

                onClick: () => {
                  setCommandSearchValue(commandSearchValue);
                  if (activeIndexRef.current.onClick) {
                    activeIndexRef.current.onClick();
                  }
                },

                hotkey: "enter",
              },
            ]}
          />
        </div>
      </div>
    </>
  );
}

export function SearchCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Search",
      description: "Search for quick actions or records...",
      getLabel: (instanceId) => {
        return "Search";
      },
      header: Header,
      component: Component,
      isHidden: true,
      commandPaletteContentClassName: SEARCH_COMMAND_PALETTE_CONTENT_CLASS,
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
